/** SUNTRONIX ONE — Compact ONE-Bar utility chip.
 *
 *  The headline chip shows the operator's *home time* — driven, in
 *  order of precedence, by:
 *    1. A per-user override saved in localStorage (`st.home.tz`) —
 *       operators picked their own city from the popover.
 *    2. The `primary` city returned by /api/world-clock/{mc_id} which
 *       reflects the customer's configured HQ time zone.
 *    3. IST (Asia/Kolkata) as a safe fallback for Suntronix operators.
 *
 *  A pulsing live dot marks the chip; other cities + FX pairs sit in
 *  the popover so the ONE Bar stays uncrowded.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Clock, Home } from "lucide-react";

const FMT = (dt, tz) => {
  try {
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit", minute: "2-digit", timeZone: tz,
    }).format(dt);
  } catch { return "—"; }
};

// Short 3-letter code for the chip.  Prefers the city's iata-ish
// abbreviation when we know it; otherwise derives from the tz.
const SHORT_TZ_CODES = {
  "Asia/Kolkata":       "IST",
  "Asia/Singapore":     "SGT",
  "Asia/Tokyo":         "JST",
  "Asia/Dubai":         "GST",
  "Europe/London":      "GMT",
  "Europe/Paris":       "CET",
  "Europe/Berlin":      "CET",
  "America/New_York":   "EST",
  "America/Los_Angeles":"PST",
  "Australia/Sydney":   "AET",
};
const shortCode = (tz) =>
  SHORT_TZ_CODES[tz] || (tz || "").split("/").pop().slice(0, 3).toUpperCase();

const HOME_TZ_STORAGE_KEY = "st.home.tz";
const IST_TZ = "Asia/Kolkata";

export function WorldClockStrip({ testId }) {
  const { user } = useAuth();
  const mcId = user?.master_company_id;
  const [cities, setCities] = useState([]);
  const [primary, setPrimary] = useState({ tz: IST_TZ, city: "Chennai" });
  const [rates, setRates] = useState(null);
  const [meta, setMeta] = useState(null);
  const [now, setNow] = useState(new Date());
  const [override, setOverride] = useState(() => {
    try { return window.localStorage.getItem(HOME_TZ_STORAGE_KEY) || null; }
    catch { return null; }
  });
  const popoverBtnRef = useRef(null);

  useEffect(() => {
    const url = mcId ? `/world-clock/${mcId}` : "/world-clock/__default__";
    api.get(url).then(({ data }) => {
      const list = data.cities || [];
      setCities(list);
      const p = list.find((c) => c.primary);
      if (p) setPrimary({ tz: p.tz, city: p.city });
    }).catch(() => setCities([]));
  }, [mcId]);

  const loadFx = useCallback(async () => {
    try {
      let base = "USD", pairs = "INR,EUR,GBP,SGD";
      if (mcId) {
        try {
          const p = await api.get(`/fx/preferred/${mcId}`);
          base = p.data.base; pairs = p.data.pairs.join(",") || pairs;
        } catch { /* fall through */ }
      }
      const { data } = await api.get("/fx/rates", { params: { base, pairs } });
      setRates(data.rates || {});
      setMeta({ base: data.base, stale: data.stale, provider: data.provider });
    } catch { setRates(null); }
  }, [mcId]);

  useEffect(() => {
    loadFx();
    const t = setInterval(loadFx, 5 * 60_000);
    return () => clearInterval(t);
  }, [loadFx]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);

  const activeTz = override || primary.tz;
  const activeCity = useMemo(() => {
    if (override) {
      const c = cities.find((x) => x.tz === override);
      return c?.city || override.split("/").pop().replace(/_/g, " ");
    }
    return primary.city;
  }, [override, cities, primary]);
  const code = shortCode(activeTz);

  const setHome = (tz) => {
    try {
      if (!tz || tz === primary.tz) {
        window.localStorage.removeItem(HOME_TZ_STORAGE_KEY);
        setOverride(null);
      } else {
        window.localStorage.setItem(HOME_TZ_STORAGE_KEY, tz);
        setOverride(tz);
      }
    } catch { /* ignore */ }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          ref={popoverBtnRef}
          className="hidden md:inline-flex items-center gap-1.5 rounded-md border border-[color:var(--st-border)] bg-[color:var(--st-surface)] hover:bg-[color:var(--st-surface-alt)] px-2 py-1 text-[11px] font-medium text-[color:var(--st-text)]"
          data-testid={testId || "world-clock-chip"}
          aria-label={`Home time (${activeCity}) and live FX`}
          title={`Home: ${activeCity} · ${activeTz}`}
        >
          <span className="relative flex h-1.5 w-1.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60"></span>
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
          </span>
          <span className="uppercase tracking-[0.14em] text-[10px] text-[color:var(--st-text-subtle)]"
            data-testid="world-clock-chip-code">
            {code}
          </span>
          <span className="font-mono" data-testid="world-clock-chip-time">
            {FMT(now, activeTz)}
          </span>
          {rates && meta && (
            <span className="hidden lg:inline text-[color:var(--st-text-subtle)] pl-2 border-l border-[color:var(--st-border)] ml-1">
              {meta.base}/INR <span className="font-mono">{Number(rates.INR || 0).toFixed(2)}</span>
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
          <Home className="h-3 w-3" /> Set home time
          {override && (
            <button className="ml-auto text-[10px] normal-case tracking-normal text-slate-500 hover:text-slate-700 underline"
              onClick={() => setHome(null)}
              data-testid="world-clock-reset-home">
              Reset to {shortCode(primary.tz)}
            </button>
          )}
        </div>
        <div className="space-y-1 mb-3 max-h-48 overflow-y-auto">
          {cities.slice(0, 8).map((c) => (
            <button key={c.tz}
              onClick={() => setHome(c.tz)}
              className={"w-full flex items-center justify-between text-[12px] rounded px-1.5 py-1 border " +
                (c.tz === activeTz
                  ? "border-emerald-500 bg-emerald-50 text-emerald-900"
                  : "border-transparent hover:bg-slate-50 text-slate-700")}
              data-testid={`clock-${c.tz}`}>
              <span className="flex items-center gap-1.5">
                {c.tz === activeTz && (
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                )}
                {c.city}
                {c.primary && (
                  <span className="text-[9px] uppercase tracking-widest text-slate-400 ml-1">
                    HQ
                  </span>
                )}
              </span>
              <span className="font-mono text-slate-900">{FMT(now, c.tz)}</span>
            </button>
          ))}
        </div>
        {rates && meta && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 border-t pt-2 flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {meta.stale ? "FX (cached)" : "Live FX"} · base {meta.base}
            </div>
            <div className="space-y-1">
              {Object.entries(rates).slice(0, 6).map(([code, val]) => (
                <div key={code} className="flex items-center justify-between text-[12px]"
                  data-testid={`fx-pair-${code}`}>
                  <span className="text-slate-500">{meta.base}/{code}</span>
                  <span className="font-mono text-slate-900">{Number(val).toFixed(2)}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Compatibility shim — the old FxTicker export is now a no-op because FX
// lives inside the World Clock chip.  Some pages still import it.
export function FxTicker() { return null; }
